/**
 * 게시판 v1 주소 — 만드는 법과 읽는 법을 한 곳에 둔다 (2026-09-15 · 결정 Q5).
 *
 * 화면 넷이 주소를 따로 갖는다. 주소는 링크(알림 · 대시보드 · 슬라이드)와 새로고침 · 뒤로가기가
 * 함께 쓰므로 **손으로 문자열을 잇지 말고 여기 함수를 부른다.**
 *
 *   /board                         → 기본 회사 게시판으로 옮김
 *   /board/:boardId                목록   ?prefix=&sort=&q=&scope=all
 *   /board/:boardId/trash          휴지통
 *   /board/:boardId/write          글쓰기
 *   /board/:boardId/settings       설정   ?tab=basic|share|post|notify
 *   /board/post/:postId            글 보기 ?tab=body|files|comments|confirm|notify
 *   /board/post/:postId/edit       글 수정 (관제 Q-A)
 *   /board/notice/:noticeId        옛 공지 id → 글로 옮김(서버 R2 뒤)
 *   /board/comment/:commentId      댓글 id → 그 글의 댓글 탭(서버 R12 뒤)
 *
 * ★ 기본값은 주소에 적지 않는다 — `?sort=latest&scope=board` 가 붙은 링크와 안 붙은 링크가
 *   같은 화면인데 다른 주소가 되면 방문 기록 · 비교가 흐려진다.
 */

export const BOARD_ROOT = '/board';

export const BOARD_SORTS = ['latest', 'views', 'comments'] as const;
export type BoardSort = (typeof BOARD_SORTS)[number];

export const BOARD_SEARCH_SCOPES = ['board', 'all'] as const;
export type BoardSearchScope = (typeof BOARD_SEARCH_SCOPES)[number];

export const BOARD_POST_TABS = ['body', 'files', 'comments', 'confirm', 'notify'] as const;
export type BoardPostTab = (typeof BOARD_POST_TABS)[number];

export const BOARD_SETTINGS_TABS = ['basic', 'share', 'post', 'notify'] as const;
export type BoardSettingsTab = (typeof BOARD_SETTINGS_TABS)[number];

export interface BoardListQuery {
  /** 말머리 id. 없으면 전체. */
  prefix: string | null;
  sort: BoardSort;
  q: string;
  scope: BoardSearchScope;
}

const DEFAULT_LIST_QUERY: BoardListQuery = { prefix: null, sort: 'latest', q: '', scope: 'board' };

/**
 * 주소의 id 를 믿기 전에 거른다 — 양의 정수만(서버 id 는 BIGINT 라 문자열로 다룬다).
 * `abc` · `0` · `-1` · `1.5` · 앞자리 0 은 null.
 */
export function parseNumericId(raw: string | null | undefined): string | null {
  if (typeof raw !== 'string') return null;
  return /^[1-9]\d{0,18}$/.test(raw) ? raw : null;
}

function pick<T extends string>(raw: string | null, allowed: readonly T[], fallback: T): T {
  return raw != null && (allowed as readonly string[]).includes(raw) ? (raw as T) : fallback;
}

export function parseBoardListQuery(sp: URLSearchParams): BoardListQuery {
  return {
    prefix: parseNumericId(sp.get('prefix')),
    sort: pick(sp.get('sort'), BOARD_SORTS, DEFAULT_LIST_QUERY.sort),
    q: (sp.get('q') ?? '').trim(),
    scope: pick(sp.get('scope'), BOARD_SEARCH_SCOPES, DEFAULT_LIST_QUERY.scope),
  };
}

/** 기본값을 뺀 쿼리 문자열 — 비면 `''`, 있으면 `?` 로 시작한다. */
export function buildBoardListSearch(query: Partial<BoardListQuery>): string {
  const q = { ...DEFAULT_LIST_QUERY, ...query };
  const sp = new URLSearchParams();
  if (q.prefix) sp.set('prefix', q.prefix);
  if (q.sort !== DEFAULT_LIST_QUERY.sort) sp.set('sort', q.sort);
  const text = q.q.trim();
  if (text) sp.set('q', text);
  // 범위는 검색어가 있을 때만 뜻이 있다.
  if (text && q.scope !== DEFAULT_LIST_QUERY.scope) sp.set('scope', q.scope);
  const s = sp.toString();
  return s ? `?${s}` : '';
}

export function parsePostTab(sp: URLSearchParams): BoardPostTab {
  return pick(sp.get('tab'), BOARD_POST_TABS, 'body');
}

export function parseSettingsTab(sp: URLSearchParams): BoardSettingsTab {
  return pick(sp.get('tab'), BOARD_SETTINGS_TABS, 'basic');
}

const tabSearch = (tab: string | undefined, fallback: string) => (tab && tab !== fallback ? `?tab=${tab}` : '');

export const boardListPath = (boardId: string, query?: Partial<BoardListQuery>) =>
  `${BOARD_ROOT}/${boardId}${query ? buildBoardListSearch(query) : ''}`;
export const boardTrashPath = (boardId: string) => `${BOARD_ROOT}/${boardId}/trash`;
export const boardWritePath = (boardId: string) => `${BOARD_ROOT}/${boardId}/write`;
export const boardSettingsPath = (boardId: string, tab?: BoardSettingsTab) =>
  `${BOARD_ROOT}/${boardId}/settings${tabSearch(tab, 'basic')}`;
export const boardPostPath = (postId: string, tab?: BoardPostTab) =>
  `${BOARD_ROOT}/post/${postId}${tabSearch(tab, 'body')}`;
export const boardPostEditPath = (postId: string) => `${BOARD_ROOT}/post/${postId}/edit`;
export const boardNoticeLinkPath = (noticeId: string) => `${BOARD_ROOT}/notice/${noticeId}`;
export const boardCommentLinkPath = (commentId: string) => `${BOARD_ROOT}/comment/${commentId}`;

/**
 * `/board` 가 열 게시판 — **기본 회사 게시판**(`systemKey='company-default'`)이 먼저,
 * 없으면 목록의 첫 게시판(서버가 회사 → 부서 → 팀 → 내 순으로 준다), 하나도 없으면 null.
 * 지금 화면(`BoardsPageWide.tsx`)이 처음 여는 게시판과 같은 규칙이다.
 */
export function pickDefaultBoardId(boards: ReadonlyArray<{ id: string; systemKey: string | null }>): string | null {
  const company = boards.find((b) => b.systemKey === 'company-default');
  return company?.id ?? boards[0]?.id ?? null;
}
